import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatINR } from "../lib/api";
import { useSEO } from "../lib/seo";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import { ChevronRight, ChevronLeft, Check, Star, Droplets, Hand, Sparkles, Gift, Plus, Minus, X } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/6ed496c7628f8f96743a6f8720b05188e37c05d595f186e373db15b5b0d4b9ff.jpeg";
const STORY_IMG = "https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/63b2c169296bbcd12d3ed179bed964f4b8dc75a4d5f94737cd95c511d4368935.jpeg";
const GIFT_IMG = "https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/4464525c265e112c7fec1c6320b87f7314614d3e5651d5ddaf2c28be7e721c22.jpeg";
const STATIONERY_IMG = "https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/382077cebdd80f8a925c3004441263c1135a95d8b5ff59776dc17bc374b98957.jpeg";
const LUGGAGE_IMG = "https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/fbe8e775d12deb304cc17ba7e81bd508cf12b6b3a2d4a46f0bcc7022d089397e.jpeg";

const WRAP = "mx-auto max-w-[1700px] px-5 sm:px-8 xl:px-14";

function SectionHead({ eyebrow, title, text, to, cta = "Shop All Products", right }) {
  return (
    <Reveal className="flex items-end justify-between gap-6 flex-wrap mb-8">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] leading-tight">{title}</h2>
        {text && <p className="text-sm text-subtext mt-2 max-w-xl">{text}</p>}
      </div>
      {right || (to && <Link to={to} className="inline-flex items-center gap-1 text-sm hover:text-coral transition-colors">{cta} <ChevronRight className="w-4 h-4" /></Link>)}
    </Reveal>
  );
}

/* 1. Image-with-text slider (hero) */
const SLIDES = [
  { eyebrow: "Little Things, Big Happiness", title: ["Finding Their", "Everyday Favourites"], text: "Joyful bottles, lunchboxes, stationery and travel buddies made for school days and small adventures.", cta: "Shop Bottles", to: "/collections/water-bottles", img: HERO_IMG, bg: "bg-sage" },
  { eyebrow: "Lunch, Sorted", title: ["Boxes They'll", "Open With A Smile"], text: "Leak-safe bento boxes with little compartments for every snack — easy for small hands to open.", cta: "Shop Lunchboxes", to: "/collections/lunchboxes", img: STORY_IMG, bg: "bg-sky" },
  { eyebrow: "Gifts Wrapped With Care", title: ["Sets That Make", "Birthdays Brighter"], text: "Ready-to-give bundles for birthdays, return gifts and little celebrations.", cta: "Shop Gift Sets", to: "/collections/gifts", img: GIFT_IMG, bg: "bg-blush" },
];

function Hero() {
  const [i, setI] = useState(0);
  const s = SLIDES[i];
  useEffect(() => { const t = setInterval(() => setI((n) => (n + 1) % SLIDES.length), 7000); return () => clearInterval(t); }, []);
  return (
    <section className={`${WRAP} pt-4 pb-10 lg:pt-10 lg:pb-20`} data-testid="hero-section">
      {/* Mobile: image on top, centred text below, dots with loading pill (Ceramide mobile hero) */}
      <div className="lg:hidden">
        <div className={`relative rounded-2xl overflow-hidden ${s.bg} aspect-[16/10]`} data-testid="hero-image-mobile">
          <AnimatePresence mode="wait">
            <motion.img key={s.img} src={s.img} alt="" width="1264" height="790" fetchPriority="high"
              initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: [0.3, 1, 0.3, 1] }}
              className="absolute inset-0 w-full h-full object-cover" />
          </AnimatePresence>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="px-2 pt-7 text-center">
            <p className="eyebrow">{s.eyebrow}</p>
            <h1 className="font-display text-[2.6rem] leading-[1.1] mt-3" data-testid="hero-heading-mobile">{s.title.join(" ")}</h1>
            <p className="text-[15px] text-subtext mt-4 max-w-sm mx-auto leading-relaxed">{s.text}</p>
            <Link to={s.to} className="btn-primary mt-6">{s.cta}</Link>
          </motion.div>
        </AnimatePresence>
        <div className="flex items-center justify-center gap-3 mt-7" aria-label="Slides">
          <button onClick={() => setI((i - 1 + SLIDES.length) % SLIDES.length)} aria-label="Previous slide" data-testid="hero-prev" className="w-10 h-10 flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
          {SLIDES.map((_, n) => (
            <button key={n} onClick={() => setI(n)} aria-label={`Slide ${n + 1}`} aria-current={n === i}
              className={`relative h-2.5 rounded-full overflow-hidden transition-all duration-500 ${n === i ? "w-20 bg-line" : "w-2.5 bg-[#C5CDD0]"}`}>
              {n === i && <span key={`fill-${i}`} className="absolute inset-y-0 left-0 bg-charcoal rounded-full hero-slide-progress" />}
            </button>
          ))}
          <button onClick={() => setI((i + 1) % SLIDES.length)} aria-label="Next slide" data-testid="hero-next" className="w-10 h-10 flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="hidden lg:grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-4 relative">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5, ease: [0.3, 1, 0.3, 1] }}>
              <p className="eyebrow" data-testid="hero-eyebrow">{s.eyebrow}</p>
              <h1 className="font-display lg:text-[3.9rem] xl:text-[4.3rem] leading-[1.08] mt-3" data-testid="hero-heading">
                {s.title.map((l) => <span key={l} className="block">{l}</span>)}
              </h1>
              <p className="mt-6 text-sm sm:text-[15px] text-subtext max-w-sm leading-relaxed">{s.text}</p>
              <Link to={s.to} data-testid="hero-shop-collection-btn" className="btn-primary mt-8">{s.cta}</Link>
            </motion.div>
          </AnimatePresence>
          <div className="hidden lg:flex items-center gap-3 mt-12" aria-label="Slides">
            <button onClick={() => setI((i - 1 + SLIDES.length) % SLIDES.length)} aria-label="Previous slide" data-testid="hero-prev-desktop" className="p-1 hover:text-coral"><ChevronLeft className="w-5 h-5" /></button>
            {SLIDES.map((_, n) => (
              <button key={n} onClick={() => setI(n)} aria-label={`Slide ${n + 1}`} className={`h-1.5 rounded-full transition-all duration-500 ${n === i ? "w-10 bg-charcoal" : "w-1.5 bg-line"}`} />
            ))}
            <button onClick={() => setI((i + 1) % SLIDES.length)} aria-label="Next slide" data-testid="hero-next-desktop" className="p-1 hover:text-coral"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className={`relative rounded-2xl overflow-hidden ${s.bg} aspect-[16/10] lg:aspect-[16/9]`} data-testid="hero-image">
            <AnimatePresence mode="wait">
              <motion.img key={s.img} src={s.img} alt={s.title.join(" ")} width="1264" height="848" fetchPriority="high"
                initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: [0.3, 1, 0.3, 1] }}
                className="absolute inset-0 w-full h-full object-cover" />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

/* 2. Collection list slider (circles) */
function CollectionCircles({ collections }) {
  const extra = [{ slug: "shop-all?sort=price-asc", name: "Sale Items", sale: true }];
  const items = [...extra, ...(collections || [])];
  return (
    <section className={`${WRAP} pb-14`} data-testid="category-section">
      <ul className="flex gap-6 sm:gap-10 overflow-x-auto no-scrollbar snap-x justify-start lg:justify-between" data-testid="category-circles">
        {items.map((c) => (
          <li key={c.slug} className="snap-start shrink-0 w-24 sm:w-28 text-center">
            <Link to={`/collections/${c.slug}`} data-testid={`category-card-${c.slug.split("?")[0]}`} className="group block">
              <div className={`w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-full overflow-hidden flex items-center justify-center ${c.sale ? "bg-coral text-white font-display text-xl" : "bg-surface"}`}>
                {c.sale ? "Sale" : <img src={c.image} alt="" width="96" height="96" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
              </div>
              <p className="text-sm mt-3 group-hover:text-coral transition-colors">{c.name}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* 3. Featured collection with tabs + rail */
const TABS = [["favourites", "Bestsellers", null], ["new", "New Arrivals", "new-arrivals"], ["bottles", "Water Bottles", "water-bottles"], ["lunchboxes", "Lunchboxes", "lunchboxes"], ["stationery", "Stationery", "stationery"]];

function Rail({ children, testid }) {
  const ref = useRef(null);
  const [prog, setProg] = useState(0);
  const onScroll = () => { const el = ref.current; if (!el) return; setProg(el.scrollLeft / Math.max(1, el.scrollWidth - el.clientWidth)); };
  const by = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div>
      <div ref={ref} onScroll={onScroll} className="flex gap-4 overflow-x-auto no-scrollbar snap-x pb-1" data-testid={testid}>{children}</div>
      <div className="flex items-center gap-6 mt-6">
        <div className="flex-1 h-px bg-line relative"><span className="absolute left-0 top-0 h-px bg-charcoal transition-all duration-300" style={{ width: `${Math.max(16, (prog || 0) * 100)}%` }} /></div>
        <div className="flex gap-2">
          <button onClick={() => by(-1)} aria-label="Scroll left" className="w-11 h-11 rounded-full border border-line flex items-center justify-center hover:bg-surface transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => by(1)} aria-label="Scroll right" className="w-11 h-11 rounded-full border border-line flex items-center justify-center hover:bg-surface transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function FeaturedTabs({ bestsellers, newArrivals }) {
  const [tab, setTab] = useState("favourites");
  const [cache, setCache] = useState({});
  const active = TABS.find((t) => t[0] === tab);
  useEffect(() => {
    if (!active?.[2] || cache[tab]) return;
    api.get(`/products?collection=${active[2]}&sort=featured`).then((r) => setCache((c) => ({ ...c, [tab]: (r.data.products || []).slice(0, 8) }))).catch(() => {});
  }, [tab, active, cache]);
  const items = tab === "favourites" ? (bestsellers || []) : tab === "new" ? (cache.new || newArrivals || []) : (cache[tab] || []);
  return (
    <section className={`${WRAP} pb-16`} data-testid="featured-section">
      <SectionHead title={active?.[1] || "New Arrivals"} text="The ones little ones reach for every single day." right={
        <div role="tablist" aria-label="Browse by need" className="flex gap-5 overflow-x-auto no-scrollbar" data-testid="need-today-tabs">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`need-tab-${id}`}
              className={`shrink-0 text-sm py-2 border-b transition-colors ${tab === id ? "border-charcoal text-charcoal" : "border-transparent text-subtext hover:text-charcoal"}`}>{label}</button>
          ))}
        </div>} />
      <Rail testid="need-today-grid">
        {items.map((p, i) => <div key={p.slug} className="snap-start shrink-0 w-[70vw] sm:w-[44vw] lg:w-[calc((100%-4rem)/5)]"><ProductCard p={p} priority={i < 2} /></div>)}
        {items.length === 0 && [0, 1, 2, 3, 4].map((i) => <div key={i} className="shrink-0 w-[calc((100%-4rem)/5)] rounded-2xl bg-surface animate-pulse aspect-[4/5]" aria-hidden="true" />)}
      </Rail>
    </section>
  );
}

/* 4. Heading + buttons with icon */
function TrustRow() {
  const items = ["Free Shipping over ₹1,299", "Cash on Delivery", "7-Day Easy Returns", "BPA-Free Materials", "Made in Thoughtful Batches"];
  return (
    <section className={`${WRAP} py-10`} data-testid="trust-section">
      <Reveal className="text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto">
          Take the time they <span className="relative inline-block italic"><span className="absolute -inset-x-2 inset-y-1 rounded-full border border-blush -rotate-2" aria-hidden="true" />deserve</span>, and let the little things shine
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="hidden sm:flex flex-wrap justify-center gap-3 mt-8">
        {items.map((t) => <span key={t} className="inline-flex items-center gap-2 border border-line rounded-full px-5 h-11 text-sm"><Check className="w-4 h-4" /> {t}</span>)}
      </Reveal>
      <div className="sm:hidden mt-8 -mx-5 overflow-hidden" data-testid="trust-marquee">
        <div className="flex gap-3 w-max animate-marquee-fast">
          {[...items, ...items].map((t, k) => <span key={k} className="inline-flex items-center gap-2 border border-line rounded-full px-5 h-12 text-[15px] whitespace-nowrap"><Check className="w-4 h-4" /> {t}</span>)}
        </div>
      </div>
    </section>
  );
}

/* 5. Countdown tile + category grid */
function useCountdown(target) {
  const [left, setLeft] = useState(target - Date.now());
  useEffect(() => { const t = setInterval(() => setLeft(target - Date.now()), 1000); return () => clearInterval(t); }, [target]);
  const s = Math.max(0, Math.floor(left / 1000));
  return [Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60];
}

function SaleAndGrid({ collections }) {
  const end = useRef(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).getTime()).current;
  const [d, h, m, s] = useCountdown(end);
  const tiles = [
    ...(collections || []).filter((c) => c.slug !== "new-arrivals").map((c) => ({ name: c.name, to: `/collections/${c.slug}`, img: c.image })),
    { name: "Under ₹499", to: "/collections/shop-all?f_price=Under%20%E2%82%B9499", img: STATIONERY_IMG },
    { name: "₹500–₹999", to: "/collections/shop-all?f_price=%E2%82%B9500%E2%80%93%E2%82%B9999", img: HERO_IMG },
    { name: "Travel Buddies", to: "/collections/luggage", img: LUGGAGE_IMG },
  ].slice(0, 8);
  return (
    <section className={`${WRAP} pb-16`} data-testid="pathways-section">
      <div className="grid lg:grid-cols-12 gap-4">
        <Reveal className="lg:col-span-4">
          <div className="relative rounded-2xl overflow-hidden min-h-[420px] h-full flex flex-col items-center justify-center text-center text-white p-8" data-testid="pathway-gifts">
            <img src={GIFT_IMG} alt="" aria-hidden="true" width="800" height="1000" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-forest/45" />
            <div className="relative">
              <p className="eyebrow text-white">Countdown Is On</p>
              <h3 className="font-display text-3xl sm:text-4xl mt-2">Back-To-School Sale</h3>
              <div className="font-display text-3xl sm:text-4xl mt-5 flex items-center justify-center gap-3" aria-label="Sale ends in" data-testid="countdown">
                {[d, h, m, s].map((n, i) => <React.Fragment key={i}>{i > 0 && <span className="text-xl">:</span>}<span className="tabular-nums">{String(n).padStart(2, "0")}</span></React.Fragment>)}
              </div>
              <Link to="/collections/shop-all?sort=price-asc" className="btn-primary bg-white text-charcoal hover:bg-surface mt-7">Shop The Sale</Link>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {tiles.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.04}>
              <Link to={t.to} data-testid={`budget-${i}`} className="group block bg-surface rounded-2xl overflow-hidden text-center pb-5 h-full">
                <div className="aspect-square overflow-hidden"><img src={t.img} alt="" width="320" height="320" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <p className="text-sm mt-3 group-hover:text-coral transition-colors">{t.name}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* 6. Promotion banner */
function PromoBanner() {
  return (
    <section className={`${WRAP} pb-16`} data-testid="gifting-section">
      <Reveal>
        <div className="bg-blush rounded-2xl px-6 sm:px-10 py-8 grid md:grid-cols-12 items-center gap-6">
          <p className="eyebrow md:col-span-3">For A Limited Time</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.7rem] md:col-span-4">Free Gift Wrapping</h2>
          <p className="text-sm text-subtext md:col-span-3">On every gift set and on orders over ₹1,299 — wrapped, tagged and ready to hand over.</p>
          <div className="md:col-span-2 md:text-right"><Link to="/collections/gifts" data-testid="hero-explore-gifts-btn" className="btn-primary">Shop Now</Link></div>
        </div>
      </Reveal>
    </section>
  );
}

/* 7. Horizontal products list */
function Trending({ products }) {
  if (!products?.length) return null;
  return (
    <section className={`${WRAP} pb-16`} data-testid="trending-section">
      <SectionHead title="Trending This Week" to="/collections/shop-all" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.slice(0, 6).map((p) => (
          <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`trending-${p.slug}`} className="group bg-surface rounded-2xl p-3 flex items-center gap-4">
            <img src={p.image} alt="" width="72" height="72" loading="lazy" className="w-16 h-16 rounded-xl object-cover bg-white" />
            <div className="flex-1 min-w-0">
              <p className="label">{(p.category || "").replace(/-/g, " ")}</p>
              <p className="text-sm truncate mt-0.5 group-hover:text-coral transition-colors">{p.name}</p>
              <p className="text-sm mt-0.5"><span className={p.mrp > p.price ? "text-coral" : ""}>{formatINR(p.price)}</span> {p.mrp > p.price && <span className="text-subtext/70 line-through ml-1">{formatINR(p.mrp)}</span>}</p>
            </div>
            <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0 group-hover:bg-forest group-hover:text-white transition-colors"><ChevronRight className="w-4 h-4" /></span>
          </Link>
        ))}
      </div>
      <div className="mt-6 text-center sm:hidden"><Link to="/collections/shop-all" className="btn-primary w-full">Show More</Link></div>
    </section>
  );
}

/* 8. Favourite products */
function Favourite({ reviews, product }) {
  const [n, setN] = useState(0);
  const list = (reviews || []).slice(0, 3);
  const review = list[n];
  if (!review || !product) return null;
  return (
    <section className="bg-sage/60 lg:bg-white" data-testid="story-section">
      <div className="grid lg:grid-cols-2">
        <div className="px-5 sm:px-8 xl:px-14 py-14 lg:py-28 flex flex-col justify-center">
          <p className="eyebrow">Our Favourite Products</p>
          <AnimatePresence mode="wait">
            <motion.div key={n} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
              <blockquote className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-tight mt-6 max-w-xl">“{review.body}”</blockquote>
              <p className="font-display text-xl mt-8">{review.name}</p>
              <p className="text-sm text-subtext mt-1">{review.title}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center gap-4 mt-8 text-sm" data-testid="favourite-controls">
            <span className="tabular-nums">{n + 1}/{list.length}</span>
            <button onClick={() => setN((n - 1 + list.length) % list.length)} aria-label="Previous review" className="w-10 h-10 rounded-full border border-line flex items-center justify-center hover:bg-white transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setN((n + 1) % list.length)} aria-label="Next review" className="w-10 h-10 rounded-full border border-line flex items-center justify-center hover:bg-white transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="relative min-h-[420px] lg:min-h-[560px]">
          <img src={STORY_IMG} alt="" aria-hidden="true" width="1264" height="848" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="w-64 sm:w-72"><ProductCard p={product} /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* 9. Two promo tiles */
function PromoTiles() {
  const tiles = [
    { eyebrow: "Holiday Gifts", title: ["Gift Sets For", "Little Celebrations"], save: "Save\n20%", to: "/collections/gifts", img: GIFT_IMG, bg: "bg-blush" },
    { eyebrow: "Ready To Roll", title: ["Trolleys Little", "Hands Can Pull"], save: "Free\nShip", to: "/collections/luggage", img: LUGGAGE_IMG, bg: "bg-sky" },
  ];
  return (
    <section className={`${WRAP} py-16`} data-testid="bundles-section">
      <div className="grid md:grid-cols-2 gap-5">
        {tiles.map((t, i) => (
          <Reveal key={t.to} delay={i * 0.08}>
            <Link to={t.to} data-testid={`bundle-card-${t.to.split("/").pop()}`} className={`group relative block rounded-2xl overflow-hidden ${t.bg} min-h-[320px] lg:min-h-[340px]`}>
              <img src={t.img} alt="" aria-hidden="true" width="800" height="500" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-[1.03] transition-transform duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/30 to-transparent" />
              <div className="relative p-7 sm:p-9 flex flex-col h-full min-h-[320px] lg:min-h-[340px]">
                <p className="eyebrow">{t.eyebrow}</p>
                <h3 className="font-display text-3xl sm:text-[2.2rem] leading-tight mt-2">{t.title.map((l) => <span key={l} className="block">{l}</span>)}</h3>
                <span className="btn-outline mt-auto self-start bg-white border-white">Shop Now</span>
              </div>
              <span className="absolute top-7 right-7 w-20 h-20 rounded-full bg-blush/90 flex items-center justify-center text-center font-display text-lg leading-tight whitespace-pre-line">{t.save}</span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* 10. Testimonials */
function Reviews({ reviews }) {
  if (!reviews?.length) return null;
  return (
    <section className={`${WRAP} pb-16`} data-testid="reviews-section">
      <SectionHead eyebrow="Happy Families" title="Hundreds of happy little customers" text="Sample reviews shown while our verified review programme starts — real reviews will replace these." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {reviews.slice(0, 4).map((r, i) => (
          <Reveal key={r.id} delay={i * 0.06}>
            <figure className="bg-surface rounded-2xl p-6 h-full flex flex-col" data-testid={`review-card-${r.id}`}>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-forest text-white flex items-center justify-center font-display text-lg">{r.name.trim()[0]}</span>
                <div className="text-sm leading-tight">
                  <p>{r.name}</p>
                  <p className="text-xs text-[#0D8756] flex items-center gap-1 mt-0.5"><Check className="w-3 h-3" /> Verified buyer</p>
                </div>
              </div>
              <div className="flex gap-0.5 mt-4" aria-label={`${r.rating} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-charcoal text-charcoal" : "text-line"}`} />)}
              </div>
              <blockquote className="mt-3 text-[15px] leading-relaxed flex-1">“{r.body}”</blockquote>
              <figcaption className="mt-5 text-xs text-subtext">On: <span className="text-charcoal">{r.title}</span></figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* 11. Shop the feed */
function Feed({ instagram }) {
  if (!instagram) return null;
  return (
    <section className={`${WRAP} pb-16`} data-testid="instagram-section">
      <SectionHead eyebrow="Shop The Feed" title={<>Follow <a href={instagram.url} target="_blank" rel="noopener noreferrer" className="hover:text-coral transition-colors">{instagram.handle}</a></>} text={instagram.note} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {instagram.images.map((img, i) => (
          <Reveal key={i} delay={i * 0.05}>
            <a href={instagram.url} target="_blank" rel="noopener noreferrer" className="block rounded-2xl overflow-hidden bg-surface aspect-square group">
              <img src={img.url} alt={img.alt} width="400" height="400" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* 12. Collapsible FAQ */
const FAQS = [
  ["How long does delivery take?", "Metro cities usually receive orders in 2–4 working days; the rest of India in 4–7. You'll get tracking by email and SMS as soon as it ships."],
  ["Is Cash on Delivery available?", "Yes — COD is available on most PIN codes for orders up to ₹5,000. Enter your PIN code on the product page to check."],
  ["Are the bottles and lunchboxes BPA-free?", "Yes. Every food-contact product is made from food-grade, BPA-free materials and we list what's inside on each page."],
  ["What is your return policy?", "Unused items can be returned within 7 days of delivery. Start a return from the Track Order page and we'll arrange the pickup."],
  ["Can I get items gift wrapped?", "Gift sets ship wrapped and tagged; for other items, add a note at checkout and we'll wrap them free on orders over ₹1,299."],
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className={`${WRAP} pb-16`} data-testid="faq-section">
      <div className="grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow">Need Help?</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] leading-tight mt-2">Discover more in our FAQ</h2>
          <p className="text-sm text-subtext mt-4">Can't find what you're looking for? <Link to="/contact" className="underline hover:text-coral">Contact us</Link> — we reply within a day.</p>
        </Reveal>
        <div className="lg:col-span-8 divide-y divide-line border-y border-line">
          {FAQS.map(([q, a], i) => (
            <div key={q}>
              <button onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} data-testid={`faq-${i}`} className="w-full flex items-center justify-between gap-4 py-5 text-left font-display text-xl sm:text-2xl">
                {q} {open === i ? <Minus className="w-5 h-5 shrink-0" /> : <Plus className="w-5 h-5 shrink-0" />}
              </button>
              <AnimatePresence initial={false}>
                {open === i && <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.3, 1, 0.3, 1] }} className="overflow-hidden text-[15px] text-subtext pb-5 pr-10">{a}</motion.p>}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
      <Reveal className="mt-10">
        <div className="relative overflow-hidden rounded-2xl bg-blush px-7 sm:px-12 py-10 grid md:grid-cols-2 items-center gap-6" data-testid="help-banner">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl">Still need help?</h3>
            <p className="text-sm text-subtext mt-2">Shoot our team a message & we'll get back to you within a day.</p>
          </div>
          <div className="md:text-right"><Link to="/contact" className="btn-primary bg-white text-charcoal hover:bg-surface">Contact Us</Link></div>
        </div>
      </Reveal>
    </section>
  );
}

/* 13. Multicolumn with icons */
function Values() {
  const cols = [[Droplets, "BPA-Free", "Food-grade materials you can trust — we list what's inside on every page."], [Hand, "Made For Little Hands", "Lids, straws and zips a four-year-old can manage on their own."], [Sparkles, "Easy To Clean", "Wide mouths, removable seals and dishwasher-safe parts."], [Gift, "Gift-Ready", "Wrapped and tagged sets for birthdays and return gifts."]];
  return (
    <section className={`${WRAP} pb-20`} data-testid="values-section">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 border-t border-line pt-12">
        {cols.map(([Icon, t, d], i) => (
          <Reveal key={t} delay={i * 0.06} className="text-center">
            <Icon className="w-9 h-9 mx-auto" strokeWidth={1.25} />
            <h3 className="font-display text-xl mt-4">{t}</h3>
            <p className="text-sm text-subtext mt-2 max-w-xs mx-auto">{d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function SpecialOffersTab() {
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("hh_offers_tab") === "0");
  if (hidden) return null;
  return (
    <div className="fixed right-0 top-1/3 z-40 flex flex-col items-center bg-coral text-white rounded-l-xl shadow-lg" data-testid="special-offers-tab">
      <Link to="/collections/shop-all?sort=price-asc" className="px-2.5 py-4 flex flex-col items-center gap-3" aria-label="Special offers for you">
        <Gift className="w-4 h-4" />
        <span className="text-xs tracking-wide" style={{ writingMode: "vertical-rl" }}>Special Offers For You</span>
      </Link>
      <button onClick={() => { sessionStorage.setItem("hh_offers_tab", "0"); setHidden(true); }} aria-label="Hide offers tab" data-testid="special-offers-close" className="pb-3 pt-1 hover:opacity-70"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  useSEO({ title: "Haven Hives — Little things, big happiness.", description: "Joyful bottles, lunchboxes, kawaii stationery and travel buddies for kids. Shop Haven Hives for their everyday adventures." });
  useEffect(() => { api.get("/home").then((r) => setData(r.data)).catch(() => {}); }, []);
  const trending = [...(data?.bestsellers || []), ...(data?.new_arrivals || [])].filter((p, i, a) => a.findIndex((x) => x.slug === p.slug) === i);

  return (
    <>
      <Hero />
      <CollectionCircles collections={data?.collections} />
      <FeaturedTabs bestsellers={data?.bestsellers} newArrivals={data?.new_arrivals} />
      <TrustRow />
      <SaleAndGrid collections={data?.collections} />
      <PromoBanner />
      <Trending products={trending} />
      <Favourite reviews={data?.reviews} product={data?.bestsellers?.[0]} />
      <PromoTiles />
      <Reviews reviews={data?.reviews} />
      <Feed instagram={data?.instagram} />
      <FAQ />
      <Values />
      <SpecialOffersTab />
    </>
  );
}
